import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { GetPayout } from "api/rpc/facility";
import Page from "components/page/Page";
import Card from "components/card/Card";
import "../facility/facility.scss";
import { StatusCode } from "api/protocols";

import { useTranslation } from "react-i18next";
import { GetPayoutTransactions, GetExportPayoutTransactions } from "api/rpc/2024-04/facilityAdmin/facility/payout";
import axios, { CancelToken } from "axios";
import moment from "moment";
import { LocaleCurrency } from "helpers/Locale";
import { capitalize } from "helpers/Helpers";
import Divider from "components/divider";
import { Badge } from "components/badge/Badge";
import TransactionsTable from "components/payoutTransactionsTable/PayoutsTable";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { ButtonNew } from "components/buttonNew";

export interface ITransaction {
  id: number;
  facility_id: number;
  amount: number;
  currency: string;
  fee: number;
  net: number;
  available_on: Date;
  processed_at: Date;
  source_type: string;
}

export interface ITransactionList {
  transactions: ITransaction[];
}
interface IFilterState {
  offset: number;
}

interface IPayoutState {
  authorization: string;
  status: string;
  currency: string;
  gross: number;
  fee: number;
  net: number;
  process_date: string;
  statement_description: string;
}

const TABLE_LIMIT = 25;
export default function Payouts(props: any) {
  const { uiActions } = props;
  const dispatch = useAppDispatch();
  const [payoutState, setPayoutState] = useState<IPayoutState>({
    authorization: "",
    status: "",
    currency: "",
    gross: null,
    fee: null,
    net: null,
    process_date: "",
    statement_description: "",
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    offset: 0,
  });

  const [transactionState, setTransactionState] = useState<ITransactionList>({
    transactions: null,
  });

  console.log("Transaction state", transactionState);
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadPayout(source.token);

    return () => {
      source.cancel();
    };
  }, []);

  async function loadPayout(token?: CancelToken) {
    const resPayout = await GetPayout({ id: Number(id) }, true);

    if (resPayout.status !== StatusCode.OK) {
      dispatch(uiActions("Error loading payout information"));
    }
    // // const currentPayout = resPayout.data.find((payout: any) => payout.id === Number(id));

    setPayoutState(prev => ({
      ...prev,
      authorization: resPayout.data[0].authorization,
      status: resPayout.data[0].status,
      currency: resPayout.data[0].currency,
      gross: resPayout.data[0].gross,
      fee: resPayout.data[0].fee,
      net: resPayout.data[0].net,
      process_date: resPayout.data[0].process_date,
      statement_description: resPayout.data[0].statement_description,
    }));

    // if (transactionState.transactions) {
    //   setTransactionState(prevState => ({ ...prevState, transactions: null }));
    // }

    const resTransactions = await GetPayoutTransactions(
      { ...filterState, limit: TABLE_LIMIT, payout_id: id },
      token ? false : true,
      token,
    );

    if (token && token.reason) {
      return;
    }

    if (resTransactions.status !== StatusCode.OK) {
      setTransactionState({ transactions: [] });
      dispatch(showError(t("Error loading transactions")));
    }

    setTransactionState({ transactions: resTransactions.data });
  }

  async function exportPayoutTransactions() {
    const exportRes = await GetExportPayoutTransactions({ ...filterState, payout_id: id }, true);

    if (exportRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error exporting payout transactions")));
      return;
    }

    const payoutExport = document.createElement("a");

    payoutExport.href = "data:text/csv;charset=utf-8," + encodeURI(exportRes.data as string);
    payoutExport.target = "_blank";
    payoutExport.download = `${payoutState.statement_description}_transactions_${moment(new Date()).format("YYYY-MM-DD").toString()}`
      .replace(/ /g, "_")
      .toLowerCase()
      .concat(".csv");
    payoutExport.click();
  }

  function handleTableNavigation(direction: "prev" | "next") {
    switch (direction) {
      case "prev": {
        setFilterState(prev => ({ ...prev, offset: filterState.offset - TABLE_LIMIT }));
        return;
      }
      case "next": {
        setFilterState(prev => ({ ...prev, offset: filterState.offset + TABLE_LIMIT }));
        return;
      }
      default:
        return;
    }
  }

  const statusBadge = (status: string) => {
    switch (status) {
      case "paid":
        return <Badge type="success">{capitalize(status.replace(/_/g, " "))}</Badge>;
      case "partially_paid":
        return <Badge type="warning">{capitalize(status.replace(/_/g, " "))}</Badge>;
      case "voided":
        return <Badge type="error">{capitalize(status.replace(/_/g, " "))}</Badge>;
      default:
        return <Badge type="gray">{capitalize(status.replace(/_/g, " "))}</Badge>;
    }
  };

  return (
    <Page
      title={t("Payouts")}
      subtitle={t("Manage payouts")}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Payouts"),
          url: "/admin/settings/payouts",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <header className="text-lg text-gray-900 font-semibold">Activity</header>
          {/* Row 1 */}
          <div className="flex flex-row pl-4 pt-4">
            <div className="flex flex-col w-1/4">
              {/* Net */}
              <span className="pb-4 text-medium text-gray-600">
                {t("Net")}:
              </span>

              <div className="flex flex-row">
                <p className="text-3xl text-semibold text-gray-900 pr-3.5">
                  <LocaleCurrency currency={payoutState.currency} amount={payoutState.net} />
                </p>
                <p className="pl-2 pt-1">{statusBadge(payoutState.status)}</p>
              </div>
            </div>

            <div className="flex flex-col w-24">
              {/* Gross */}
              <span className="pb-2 text-gray-600">{t("Gross")}:</span>
              <p className="text-semibold text-lg text-gray-900">
                <LocaleCurrency currency={payoutState.currency} amount={payoutState.gross} />
              </p>
            </div>

            <div className="flex flex-col">
              {/* Fee */}
              <span className="pb-2 text-gray-600">{t("Fee")}:</span>
              <p className="text-semibold text-lg text-gray-900">
                <LocaleCurrency currency={payoutState.currency} amount={payoutState.fee} />
              </p>
            </div>
          </div>

          {/* Row 2 */}
          <div className="flex flex-row pl-4 pt-2.5">
            <div className="flex pt-3 flex-col w-1/4">
              {/* Process Date */}
              <span>{t("Process Date")}:</span>
              <p>{moment(payoutState.process_date).format("LL")}</p>
            </div>

            <div className="flex flex-col w-24">
              {/* Currency */}
              <span className="pb-2 text-gray-600">{t("Currency")}:</span>
              <p className="text-semibold text-lg text-gray-900">{capitalize(payoutState.currency)}</p>
            </div>

            <div className="flex flex-col">
              {/* Authorization */}
              <span className="pb-2 text-gray-600">{t("Authorization")}:</span>{" "}
              <p className="text-semibold text-lg text-gray-900">{payoutState.authorization}</p>
            </div>

            <div className="flex flex-col pl-3">
              {/* Statement Description */}
              <span className="pb-2 text-gray-600">{t("Statement Description")}:</span>{" "}
              <p className="text-semibold text-lg text-gray-900">{payoutState.statement_description}</p>
            </div>
          </div>
          <Divider />
          <div className="flex justify-between pb-6">
            <header className="text-lg text-gray-900 font-semibold">{t("Transactions")}</header>
            <ButtonNew onClick={() => exportPayoutTransactions()}>{t("Export Transactions")}</ButtonNew>
          </div>
          
          <Card>
            <TransactionsTable
              payOuts={transactionState}
              tableLimit={TABLE_LIMIT}
              tableOffset={filterState.offset}
              handleTableOffset={direction => handleTableNavigation(direction)}
            />
          </Card>
        </Card.Section>
      </Card>
    </Page>
  );
}
